import Component from '@glimmer/component'
import type { ThemeKeys } from 'teacher-ui-client/utils/themes'
import { service } from '@ember/service'
import { task } from 'ember-concurrency'
import { trackedFunction } from 'reactiveweb/function'
import { action } from '@ember/object'
import { tracked } from '@glimmer/tracking'
import type Store from '@ember-data/store'
import type AnalyticsService from 'teacher-ui-client/services/analytics'
import type BookLibraryBook from 'teacher-ui-client/models/book-library/book'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type { IntlService } from 'ember-intl'
import { LayoutBlocksModal } from 'teacher-ui-client/components/layout-blocks/modal'
import { FidgetLoadingIndicatorComponent } from '@blakeelearning/fidget/components/fidget/loading-indicator'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { BookLibraryBookCard } from 'teacher-ui-client/components/book-library/book-card'
import { BookLibraryReadBookLink } from 'teacher-ui-client/components/book-library/read-book-link'
import { LinkTo } from '@ember/routing'
import { TabGroupComponent } from 'teacher-ui-client/components/tab-group'
import { BookModalBookDetails } from 'teacher-ui-client/components/book-library/book-modal/book-details'
import { BookModalDescription } from 'teacher-ui-client/components/book-library/book-modal/description'
import { BookLibraryResources } from 'teacher-ui-client/components/book-library/resources'
import { t } from 'ember-intl'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import perform from 'ember-concurrency/helpers/perform'
import { hash } from '@ember/helper'
import { TAB_TYPE_KEYS } from 'teacher-ui-client/components/tab-group/tab-types'
import type BookLibraryCollection from 'teacher-ui-client/models/book-library/collection'

export const ModalTab = {
  bookDetails: 'bookDetails',
  description: 'description',
  resources: 'resources',
}
export type ModalTab = ValuesOf<typeof ModalTab>

interface BookModalArgs {
  bookId?: string
  isSubco?: boolean
  closeBookModal: () => void
  collections: BookLibraryCollection[]
  theme?: ThemeKeys
  showBookModal?: boolean
}

export class BookModal extends Component<BookModalArgs> {
  @service store!: Store

  @service analytics!: AnalyticsService

  @service flashQueue!: FlashQueueService

  @service intl!: IntlService

  @queryParam('subscription-type/book-library') bookTab!: ModalTab

  @tracked localBookTab: ModalTab = ModalTab.bookDetails

  get activeTab() {
    return this.bookTab ?? this.localBookTab ?? ModalTab.bookDetails
  }

  get isBookDetailsActive() {
    return this.activeTab === ModalTab.bookDetails
  }

  get isDescriptionActive() {
    return this.activeTab === ModalTab.description
  }

  get isResourcesActive() {
    return this.activeTab === ModalTab.resources
  }

  fetchBook = trackedFunction(this, async () => {
    const { bookId } = this.args
    const { analytics } = this
    if (!bookId) return

    await Promise.resolve() // disconnect tracking before store calls

    const book = await this.store.findRecord<BookLibraryBook>('book-library/book', bookId)
    if (book) {
      analytics.capture('Teacher Library - book modal opened', {
        bookId,
        bookCode: book.code,
      })
    }
    return book
  })

  toggleBookExclude = task(async (book: BookLibraryBook) => {
    book.exclude = !book.exclude
    await book.save()
    const message = this.intl.t('bookLibrary.components.bookModal.excludeBook.success', {
      name: book.title,
      nowExcluded: book.exclude,
    })
    this.flashQueue.addSuccess({ subtitle: message })
  })

  @action
  changeTab(tab: ModalTab, event?: Event) {
    if (event) {
      event.preventDefault()
    }
    this.bookTab = tab
    // Also update local state in case we are using the modal somewhere without query params
    this.localBookTab = tab
  }

  <template>
    <LayoutBlocksModal class="w-3xl" @close={{@closeBookModal}} @showModal={{@showBookModal}}>
      {{#if this.fetchBook.isPending}}
        <FidgetLoadingIndicatorComponent
          data-test-book-modal-loading
          @show={{true}}
          @centered={{true}}
          @appearance="small"
          @overlay={{false}}
        />
      {{else if this.fetchBook.isError}}
        <p data-test-error class="mb-3 px-4">
          {{t "dataLoad.error" subject=(t "bookLibrary.book.singular")}}
        </p>
      {{else if this.fetchBook.value}}
        {{#let this.fetchBook.value as |book|}}
          {{#if @isSubco}}
            <label class="absolute top-0 m-4 inline-flex cursor-pointer items-center gap-2 font-normal select-none">
              <input
                data-test-book-modal-exclude
                type="checkbox"
                class="cursor-pointer"
                checked={{book.exclude}}
                {{on "input" (perform this.toggleBookExclude book)}}
              />
              {{t "bookLibrary.components.bookModal.excludeBook.label"}}
              <Tooltip @text={{t "bookLibrary.components.bookModal.excludeBook.tooltip"}} />
            </label>
          {{/if}}
          <div class="flex flex-col text-center" data-test-book-modal-loaded>
            <BookLibraryBookCard
              data-test-book-modal-card
              class="self-center"
              @book={{book}}
              @collections={{@collections}}
              @showResourcesPill={{false}}
            />
            {{#unless book.exclude}}
              <BookLibraryReadBookLink @buttonLabel={{t "bookLibrary.readBookInLibrary"}} @book={{book}} />
            {{/unless}}
            {{#unless book.exclude}}
              <p class="mb-3 flex items-center justify-center space-x-4">
                <LinkTo
                  data-test-create-read-aloud
                  @route="assignments.product.create"
                  @model="reading-eggspress"
                  @query={{hash activityType="reading-eggspress-library-read-aloud" bookCode=book.code}}
                  class="text-dusty-black-250 mt-2 text-base uppercase underline"
                >
                  {{t "assignments.library.assignReadAloud"}}
                </LinkTo>
                <LinkTo
                  data-test-create-book-assignment
                  @route="assignments.product.create"
                  @model="reading-eggspress"
                  @query={{hash activityType="reading-eggspress-library-quiz" bookCode=book.code}}
                  class="text-dusty-black-250 mt-2 text-base uppercase underline"
                >
                  {{t "assignments.library.assignBook"}}
                </LinkTo>
              </p>
            {{/unless}}
            <TabGroupComponent class="mx-24 mt-6" @theme={{@theme}} @tabType={{TAB_TYPE_KEYS.underline}} as |tabGroup|>
              <tabGroup.tab
                data-test-tab-details
                @isActive={{this.isBookDetailsActive}}
                {{on "click" (fn this.changeTab ModalTab.bookDetails)}}
              >
                {{t "bookLibrary.components.bookModal.bookDetails"}}
              </tabGroup.tab>
              <tabGroup.tab
                data-test-tab-description
                @isActive={{this.isDescriptionActive}}
                {{on "click" (fn this.changeTab ModalTab.description)}}
              >
                {{t "bookLibrary.components.bookModal.description"}}
              </tabGroup.tab>
              <tabGroup.tab
                data-test-tab-resources
                @isActive={{this.isResourcesActive}}
                {{on "click" (fn this.changeTab ModalTab.resources)}}
              >
                {{t "bookLibrary.components.bookModal.resources"}}
              </tabGroup.tab>
            </TabGroupComponent>
            <div class="mt-3 h-96 overflow-y-auto pt-5 shadow-inner">
              <BookModalBookDetails
                data-test-details-tab-content
                class="mx-20 text-left {{unless this.isBookDetailsActive 'hidden'}}"
                @book={{book}}
              />
              <BookModalDescription
                data-test-description-tab-content
                class="mx-20 text-left {{unless this.isDescriptionActive 'hidden'}}"
                @description={{book.description}}
              />
              <BookLibraryResources
                data-test-resources-tab-content
                class="mx-20 text-left {{unless this.isResourcesActive 'hidden'}}"
                @bookCode={{book.code}}
                @theme={{@theme}}
              />
            </div>
          </div>
        {{/let}}
      {{else}}
        <p data-test-not-found class="mb-3 px-4">
          {{t "dataLoad.notFound" subject=(t "bookLibrary.book.singular")}}
        </p>
      {{/if}}
    </LayoutBlocksModal>
  </template>
}

export default BookModal
